import { Dispatch, useCallback } from 'react'
import clone from 'clone'
import deepEqual from 'react-fast-compare'
import { INotificationBuilderState } from './interfaces/notification-builder-state.interface'
import {
    addMultipleVariants,
    addVariant,
    getNextVariantWithDefaultNativeAndroidChannel,
    isAllFeatTest,
    isSingleContent,
    isSingleDelivery,
    parseLoadedVariant,
    removeAllOtherVariants,
    resetVariantDistributionMap,
    rmVariant,
} from './helpers'
import {
    ActionDispatcher,
    AddMultiVariantActionPack,
    AddVariantActionPack,
    DispatchAfterEffect,
    NotificationDispatchActionPack,
    OverrideBuilderStateActionPack,
    PatchAudienceActionPack,
    PatchAvailableDomainsActionPack,
    PatchAvailableSegmentsActionPack,
    PatchBuilderStateActionPack,
    PatchChannelDependenciesActionPack,
    PatchChannelDependencyLoadingActionPack,
    PatchDeliveryActionPack,
    PatchDeliveryChannelActionPack,
    PatchReachEstimateActionPack,
    PatchSelectedTemplateActionPack,
    PatchTemplateDetailsActionPack,
    PatchTestActionPack,
    PatchVariantActionPack,
    RemoveOtherVariantsActionPack,
    RemoveVariantActionPack,
    SetStzAvailabilityCacheActionPack,
    SetStzAvailabilityValidatingActionPack,
} from './types'
import { GetNotificationResponse } from '@pushly/models/lib/structs/notifications'
import { INotificationTemplateWebChannel, INotificationTemplateNativeAndroidChannel } from '@pushly/models/lib/structs/notifications/notification-template-interfaces'
import { parseNotificationResponse } from '../../features/notifications/helpers/notification-helpers'
import { NotificationDeliveryType } from '../../enums/notification-delivery-type'
import { NotificationDeliveryWindow } from '../../enums/notification-delivery-window'
import { NotificationAudienceModel } from '../../models/notification/notification-audience.model'
import { AppService } from '../../services'
import { NotificationBuilderLevel } from './enums'
import { Container } from 'typescript-ioc'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { AppState } from '../../stores/app'
import { NotificationAbTestType } from '@pushly/models/lib/enums/notification-ab-test-type'

let reducerAfterEffectDebounceTimeout: NodeJS.Timeout

export function useActionDispatcher(
    dispatcher: Dispatch<NotificationDispatchActionPack & { afterEffect: DispatchAfterEffect }>,
    afterEffect?: (builder: INotificationBuilderState) => void,
): ActionDispatcher {
    return useCallback(
        (action: NotificationDispatchActionPack) => {
            dispatcher({
                ...action,
                afterEffect: (state) => {
                    afterEffect?.(state)
                },
            })
        },
        [dispatcher, afterEffect],
    )
}

export function getBuilderStateReducer(
    state: INotificationBuilderState,
    action: NotificationDispatchActionPack & { afterEffect: DispatchAfterEffect },
) {
    // ensure clone of current state is used to prevent
    // possible active-state mutations
    let currentState = clone(state)

    let update: INotificationBuilderState
    switch (action.entity) {
        case 'root':
            update = handleRootStateReducerRequest(currentState, action)
            break
        case 'audience':
            update = handleAudienceReducerRequest(currentState, action)
            break
        case 'delivery':
            update = handleDeliveryReducerRequest(currentState, action)
            break
        case 'variant':
            update = handleVariantReducerRequest(currentState, action)
            break
        case 'variants':
            update = handleVariantsReducerRequest(currentState, action)
            break
        case 'test':
            update = handleTestReducerRequest(currentState, action)
            break
        case 'reach-estimate':
            update = handleReachEstimateReducerRequest(currentState, action)
            break
        case 'available-domains':
            update = handleAvailableDomainsReducerRequest(currentState, action)
            break
        case 'available-segments':
            update = handleAvailableSegmentsReducerRequest(currentState, action)
            break
        case 'channel':
            update = handleDeliveryChannelsReducerRequest(currentState, action)
            break
        case 'channel-dependencies':
            update = handleChannelDependenciesReducerRequest(currentState, action)
            break
        case 'loading-channel-dependencies':
            update = handleChannelDependenciesLoadingReducerRequest(currentState, action)
            break
        case 'detail':
            update = handleTemplateDetailsReducerRequest(currentState, action)
            break
        case 'selected-template':
            update = handleSelectedTemplateReducerRequest(currentState, action)
            break
        case 'stz-availability-validating':
            update = handleStzAvailabilityValidatingUpdate(currentState, action)
            break
        case 'stz-availability-cache':
            update = handleStzAvailabilityCacheReducerRequest(currentState, action)
            break

        default:
            const err = new Error(`Encountered unknown dispatch action: ${action}`)
            console.warn(err)
            throw err
    }

    // Schedule any possible after effects
    const { afterEffect } = action
    clearTimeout(reducerAfterEffectDebounceTimeout)
    reducerAfterEffectDebounceTimeout = setTimeout(() => {
        afterEffect(update)
    }, 10)

    return update
}

function handleRootStateReducerRequest(
    state: INotificationBuilderState,
    action: OverrideBuilderStateActionPack | PatchBuilderStateActionPack,
): INotificationBuilderState {
    switch (action.type) {
        case 'put':
            state = action.data
            break

        case 'patch':
            state = {
                ...state,
                ...action.data,
            }
            break
    }

    return state
}

function handleVariantReducerRequest(
    state: INotificationBuilderState,
    action: PatchVariantActionPack,
): INotificationBuilderState {
    const variantId = action.data.id
    if (variantId >= state.variants.length || variantId < 0) {
        console.warn(`[NotificationBuilderReducer] Variant ID out of range: ${variantId}`)
        return state
    }

    const curr = state.variants[variantId]
    if (!curr) {
        console.warn(`[NotificationBuilderReducer] Variant ID invalid: ${variantId}`)
        return state
    }

    const next = action.data.variant
    const testTypes = state.test?.getTestTypes()

    if (isSingleContent(testTypes)) {
        state.variants.forEach((v) => v.setContent(next.getContent()))
    } else {
        curr.setContent(next.getContent())
    }

    if (isSingleDelivery(testTypes)) {
        state.variants.forEach((v) => v.setDelivery(next.getDelivery()))
    } else {
        curr.setDelivery(next.getDelivery())
    }

    if (curr.getId() !== next.getId()) {
        state.variants[variantId] = state.variants[variantId].clone(false)
    }

    return state
}

function handleVariantsReducerRequest(
    state: INotificationBuilderState,
    action: AddVariantActionPack | AddMultiVariantActionPack | RemoveVariantActionPack | RemoveOtherVariantsActionPack,
): INotificationBuilderState {
    switch (action.type) {
        case 'add':
            return addVariant(action.data.variant, state)
        case 'add-multi':
            return addMultipleVariants(action.data.variants, state)
        case 'remove':
            return rmVariant(action.data.indexToRemove, state)
        case 'remove-others':
            return removeAllOtherVariants(state, action.data.indexToKeep)
    }

    return state
}

function handleAudienceReducerRequest(
    state: INotificationBuilderState,
    action: PatchAudienceActionPack,
): INotificationBuilderState {
    const domain = action.data.domain
    const next = action.data.audience

    for (const variant of state.variants) {
        variant.setAudience(next)

        const content = variant.getContent().getDefaultContent()
        const segmentIcon = next.getDefaultIconUrl()
        const isNotUsingIcon = !content.getIconUrl()
        const isUsingDomainIcon = content.getIconUrl() === domain.defaultIconUrl
        const isUsingSegmentIcon = !!content.getSegmentDefaultIconUrl()

        if (segmentIcon && (isNotUsingIcon || isUsingDomainIcon || isUsingSegmentIcon)) {
            content.setIconUrl(next.getDefaultIconUrl(), true)
        } else if (!segmentIcon && isUsingSegmentIcon) {
            content.setIconUrl(domain.defaultIconUrl)
        }
    }

    return state
}

function handleDeliveryReducerRequest(
    state: INotificationBuilderState,
    action: PatchDeliveryActionPack,
): INotificationBuilderState {
    const { delivery, variantIdx } = action.data

    if (variantIdx === 'all') {
        state.variants.forEach((v) => v.setDelivery(delivery))
    } else {
        const testTypes = state.test?.getTestTypes()
        if (testTypes && testTypes.length === 1 && testTypes.includes(NotificationAbTestType.CONTENT)) {
            throw new Error('Cannot change delivery for single variant in a content only test.')
        }

        state.variants[variantIdx].setDelivery(delivery)
    }

    return state
}

function handleTestReducerRequest(
    state: INotificationBuilderState,
    action: PatchTestActionPack,
): INotificationBuilderState {
    const next = action.data

    const prevTestTypes = state.test?.getTestTypes() ?? []
    const nextTestTypes = next?.getTestTypes() ?? []

    const sourceVariant = state.variants[state.selectedVariantIdx]
    if (!next) {
        state = removeAllOtherVariants(state)
    }

    state.test = next

    if (next) {
        if (
            !deepEqual(prevTestTypes, nextTestTypes) &&
            ((prevTestTypes.length > 1 && !isAllFeatTest(nextTestTypes)) ||
                isSingleContent(nextTestTypes) ||
                isSingleDelivery(nextTestTypes))
        ) {
            state.variants = [sourceVariant]
            resetVariantDistributionMap(state)
        }
    }

    if (!next || !isAllFeatTest(next.getTestTypes())) {
        state.selectedVariantIdx = 0
    }

    return state
}

function handleReachEstimateReducerRequest(
    state: INotificationBuilderState,
    action: PatchReachEstimateActionPack,
): INotificationBuilderState {
    state.reachEstimateLoaded = true
    state.reachEstimate = action.data
    return state
}

function handleAvailableDomainsReducerRequest(
    state: INotificationBuilderState,
    action: PatchAvailableDomainsActionPack,
): INotificationBuilderState {
    state.availableDomainsLoaded = true
    state.availableDomains = action.data
    return state
}

function handleAvailableSegmentsReducerRequest(
    state: INotificationBuilderState,
    action: PatchAvailableSegmentsActionPack,
): INotificationBuilderState {
    state.availableSegmentsLoaded = true
    state.availableSegments = action.data
    return state
}

function handleDeliveryChannelsReducerRequest(
    state: INotificationBuilderState,
    action: PatchDeliveryChannelActionPack,
): INotificationBuilderState {
    const prevChannels = state.channels
    if (
        !prevChannels.includes(DeliveryChannel.NATIVE_ANDROID) &&
        action.data.includes(DeliveryChannel.NATIVE_ANDROID) &&
        state.channelDependencies[DeliveryChannel.NATIVE_ANDROID].notificationChannels.length > 0 &&
        state.mode === 'create'
    ) {
        const nextVariant = getNextVariantWithDefaultNativeAndroidChannel(
            state,
            state.channelDependencies[DeliveryChannel.NATIVE_ANDROID].notificationChannels,
        )
        state.variants.splice(state.selectedVariantIdx, 1, nextVariant)
    }

    state.channels = action.data

    return state
}

function handleChannelDependenciesLoadingReducerRequest(
    state: INotificationBuilderState,
    action: PatchChannelDependencyLoadingActionPack,
): INotificationBuilderState {
    state.channelDependencies[action.data.channel].loading = action.data.loading
    return state
}

function handleChannelDependenciesReducerRequest(
    state: INotificationBuilderState,
    action: PatchChannelDependenciesActionPack,
): INotificationBuilderState {
    const update = action.data.value

    state.channelDependencies[action.data.channel] = {
        ...state.channelDependencies[action.data.channel],
        ...update,
    }

    // once loaded if the android content does not have a channel set AND the domain has a default native android channel we should set it
    if (state.mode === 'create') {
        const nextVariant = getNextVariantWithDefaultNativeAndroidChannel(
            state,
            state.channelDependencies[DeliveryChannel.NATIVE_ANDROID].notificationChannels,
        )
        state.variants.splice(state.selectedVariantIdx, 1, nextVariant)
    }

    return state
}

function handleTemplateDetailsReducerRequest(
    state: INotificationBuilderState,
    action: PatchTemplateDetailsActionPack,
): INotificationBuilderState {
    state.details = action.data
    return state
}

function handleSelectedTemplateReducerRequest(
    state: INotificationBuilderState,
    action: PatchSelectedTemplateActionPack,
): INotificationBuilderState {
    state.usingTemplate = action.data.usingTemplate

    // short circuit if usingTemplate hasn't been set yet (maintain form state until a template overrides)
    if (!action.data.usingTemplate && state.selectedTemplate === undefined) {
        return state
    }

    state.selectedTemplate = action.data.id
    const template = state.availableTemplates.find((t) => action.data.id === t.id)
    let notificationVariant
    if (template) {
        const { name, description, templateContent, templateSegmentIds, templateExcludedSegmentIds, templateTtlSpec } =
            template

        notificationVariant = parseNotificationResponse({
            delivery_spec: {
                type: NotificationDeliveryType.IMMEDIATE,
                window: NotificationDeliveryWindow.STANDARD,
                ...templateTtlSpec,
            },
            audience: {
                segment_ids: templateSegmentIds?.length ? Array.from(templateSegmentIds) : undefined,
                excluded_segment_ids: templateExcludedSegmentIds?.length
                    ? Array.from(templateExcludedSegmentIds)
                    : undefined,
            },
            template: {
                channels: {
                    default: {
                        ...templateContent.default,
                        actions:
                            templateContent.default.overrideDefaultActions === false
                                ? undefined
                                : templateContent.default.actions,
                        autoSuggestedFieldsResults: undefined,
                    },
                    web: templateContent.web satisfies INotificationTemplateWebChannel | undefined,
                    native_ios: templateContent.nativeIos,
                    native_android: templateContent.nativeAndroid as
                        | INotificationTemplateNativeAndroidChannel
                        | undefined,
                },
                keywords: template.keywords?.length ? Array.from(template.keywords) : [],
                name,
                description,
            },
        })

        const currVariant = state.variants[state.selectedVariantIdx]
        // keep variant ID to maintain clean notification form render process - only changes are content
        notificationVariant.id = currVariant.getId()
        const nextVariant = parseLoadedVariant(notificationVariant)

        state.variants.splice(state.selectedVariantIdx, 1, nextVariant)
        state.channels = templateContent.default.channels.length ? Array.from(templateContent.default.channels) : []
        state.usingTemplate = action.data.usingTemplate
    } else if (!action.data.usingTemplate) {
        const userPreferences = action.userPreferences

        const initialVariant = action.getInitialVariant(
            action.data.domain,
            undefined,
            state,
            userPreferences,
            action.onInitialVariantInit,
        )

        // reset controlled fields via properties
        initialVariant.setContent({
            ...initialVariant.getContent().serialize(),
            defaultContent: {
                ...initialVariant.getContent().getDefaultContent(),
                _custom_actions_enabled: false,
            },
        })

        if (state.allPushSegment) {
            initialVariant.setAudience(NotificationAudienceModel.build({ segmentIds: [state.allPushSegment.id] }))
        }
        state.variants = [initialVariant]
        state.usingTemplate = action.data.usingTemplate
        // return channel state to user preference set
        state.channels = userPreferences.get(['last_targeted_delivery_channels'])
    }

    return state
}

function handleStzAvailabilityValidatingUpdate(
    state: INotificationBuilderState,
    action: SetStzAvailabilityValidatingActionPack,
): INotificationBuilderState {
    state.stzAvailabilityValidating = action.data
    return state
}

function handleStzAvailabilityCacheReducerRequest(
    state: INotificationBuilderState,
    action: SetStzAvailabilityCacheActionPack,
): INotificationBuilderState {
    switch (action.type) {
        case 'set': {
            state.stzAvailabilityValidating = false
            state.stzAvailabilityCache.set(action.key, action.data)
            break
        }
    }

    return state
}
